// Forked from angular universal
/**
 * The purpose of this interceptor is to watch for any calls made using the httpclient on the
 * server, store the results in the transferstate, so that these calls will not be repeated
 * on the client.
 */

/**
 * @license
 * Copyright Google LLC All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */
import {
    HttpEvent,
    HttpHandler,
    HttpHeaders,
    HttpInterceptor,
    HttpRequest,
    HttpResponse
  } from '@angular/common/http';
  import {ApplicationRef, Injectable} from '@angular/core';
  import { TransferState, makeStateKey} from '@angular/platform-browser';
  import {Observable} from 'rxjs';
  import {tap, take, filter} from 'rxjs/operators';
import { LoggerService } from '../logger/logger.service';

import { of } from 'rxjs';

export interface TransferHttpResponse {
  body?: any | null;
  headers?: {[k: string]: string[]};
  status?: number;
  statusText?: string;
  url?: string;
}

function getHeadersMap(headers: HttpHeaders) {
  const headersMap: {[name: string]: string[]} = {};
  for (const key of headers.keys()) {
    headersMap[key] = headers.getAll(key)!;
  }
  return headersMap;
}

const SKIPPED_URLS = [
  'assets/i18n'
];

const BODY_CLONED_URLS = [
  '/listings/'
];

@Injectable()
export class RmTransferHttpCacheInterceptor implements HttpInterceptor {

  private isCacheActive = true;

  private invalidateCacheEntry(url: string) {
    this.transferState.remove(makeStateKey<TransferHttpResponse>('G.' + url));
    this.transferState.remove(makeStateKey<TransferHttpResponse>('H.' + url));
  }

  constructor(appRef: ApplicationRef, private transferState: TransferState, private loggerService: LoggerService) {
    // Stop using the cache if the application has stabilized, indicating initial rendering is
    // complete.
    appRef.isStable
      .pipe(
        filter((isStable: boolean) => isStable),
        take(1)
      ).toPromise()
      .then(() => { this.isCacheActive = false; });
  }

  static getUrlWithoutHost(url: string): string {
    // assume routes start with http/s://
    let endOfProtocol = 0;
    if (url.indexOf('https://') === 0) {
      endOfProtocol = 8;
    } else if (url.indexOf('http://') === 0) {
      endOfProtocol = 7;
    } else {
      return url;
    }

    const endOfHost = url.indexOf('/', endOfProtocol);
    return endOfHost >= 0 ? url.substr(endOfHost) : url;
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

    this.loggerService.logDebug('httpRequests', req.method, 'request to', req.urlWithParams);

    // Stop using the cache if there is a mutating call.
    if (req.method !== 'GET' && req.method !== 'HEAD') {
      this.invalidateCacheEntry(req.url);
    }

    if (!this.isCacheActive || SKIPPED_URLS.some(u => req.url.indexOf(u) >= 0)) {
      // Cache is no longer active. Pass the request through.
      return next.handle(req);
    }

    // Make the key not include the host, the server and client may use different server base but we still want to transfer cache on those
    const key = (req.method === 'GET' ? 'G.' : 'H.') + RmTransferHttpCacheInterceptor.getUrlWithoutHost(req.url) + req.params.toString();
    const storeKey = makeStateKey<TransferHttpResponse>(key);



    if (this.transferState.hasKey(storeKey)) {
      // Request found in cache. Respond using it.
      const response = this.transferState.get(storeKey, {} as TransferHttpResponse);
      return of(new HttpResponse<any>({
        body: response.body,
        headers: new HttpHeaders(response.headers),
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      }));
    } else {
      // Request not found in cache. Make the request and cache it.
      const httpEvent = next.handle(req);
      return httpEvent
        .pipe(
          tap((event: HttpEvent<any>) => {
            if (event instanceof HttpResponse) {
              let body = event.body;
              if (BODY_CLONED_URLS.some(u =>  event.url.indexOf(u) >= 0)) {
                // Certain urls must have response body cloned since the client will start manipulating the body.
                // console.log("Making clone of intercepted url " + event.url);
                body = JSON.parse(JSON.stringify(body));
              }

              this.transferState.set(storeKey, {
                body: body,
                headers: getHeadersMap(event.headers),
                status: event.status,
                statusText: event.statusText,
                url: event.url!,
              });
            }
          })
        );
    }
  }
}

